<template>
  <!-- 直接渲染 PNG 图标（包括通过 name 解析得到的 URL 和直接传入的 src） -->
  <img
    v-if="iconUrl"
    :src="iconUrl"
    :class="iconClass"
    :style="iconStyle"
    v-bind="$attrs"
  />
  
  <!-- 渲染基于 Element-Plus 的图标组件 -->
  <component 
    :is="iconComponent" 
    v-else-if="iconComponent"
    :class="iconClass"
    :style="iconStyle"
    v-bind="$attrs"
  />

  <!-- 加载占位符（极短暂态） -->
  <div 
    v-else-if="loading"
    class="icon-placeholder"
    :class="iconClass"
    :style="iconStyle"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { dynamicImports } from '@/utils/lazyLoader'

interface Props {
  /** 图标名称（当未传 src 时使用） */
  name?: string
  /** 直接传入图标地址（优先级高于 name） */
  src?: string
  /** 图标类型：'png' | 'element' */
  type?: 'png' | 'element'
  /** 图标类名 */
  class?: string
  /** 图标样式 */
  style?: Record<string, any>
  /** 图标大小 */
  size?: string | number
}

const props = withDefaults(defineProps<Props>(), {
  type: 'png',
  size: '16px',
  name: ''
})

const loading = ref(true)
const iconComponent = ref<any>(null)
const iconUrl = ref<string | null>(null)

// 计算图标类名
const iconClass = computed(() => {
  const classes = ['lazy-icon']
  if (props.class) classes.push(props.class)
  return classes.join(' ')
})

// 计算图标样式
const iconStyle = computed(() => {
  const style: Record<string, any> = {
    width: typeof props.size === 'number' ? `${props.size}px` : props.size,
    height: typeof props.size === 'number' ? `${props.size}px` : props.size,
    ...props.style
  }
  return style
})

// 加载图标（src 优先，其次通过 dynamicImports）
const loadIcon = async () => {
  try {
    loading.value = true
    iconComponent.value = null
    iconUrl.value = null

    // 1) 若直接传入 src，则直接渲染 img
    if (props.src) {
      iconUrl.value = props.src
      return
    }

    // 2) 否则根据 name 和 type 动态加载
    if (!props.name) return

    const result = await dynamicImports.importIcon(props.name, props.type)

    if (props.type === 'png') {
      // 解析为 URL
      if (typeof result === 'string' && result) {
        iconUrl.value = result
      } else {
        iconUrl.value = null
      }
    } else if (props.type === 'element') {
      if (result) {
        iconComponent.value = result
      }
    }
  } catch (error) {
    console.warn(`Failed to load icon: ${props.name || props.src}`, error)
  } finally {
    loading.value = false
  }
}

onMounted(loadIcon)

// 当 name/type/src 变化时，重新加载图标
watch(() => [props.name, props.type, props.src], loadIcon)
</script>

<style scoped>
.lazy-icon {
  display: inline-block;
  vertical-align: middle;
}

.icon-placeholder {
  background-color: #f0f0f0;
  border-radius: 2px;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}
</style>